import { ApparelProductCard } from '@apparel/ui/views/productssearch/ProductCard';
import { Currency } from '@components/Currency';
import { CustomizableWheel } from '@components/CustomizableWheel';
import { Flex, InlineFlex } from '@components/Layout/Blocks';
import { color } from '@lib/style/theme';
import { classList } from '@lib/utils';
import ButtonBase from '@material-ui/core/ButtonBase';
import Icon from '@material-ui/core/Icon/Icon';
import Typography from '@material-ui/core/Typography';
import { PackageType } from '@model/Package';
import { SkuType } from '@model/Sku';
import { call } from '@packages/functions';
import { baseFilter } from '@packages/functions/lib/baseFilter';
import { LazyImage } from '@packages/image/LazyImage';
import { Absolute } from '@packages/layout/lib/Blocks';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import React, { HTMLAttributes, useCallback } from 'react';
import styled from 'styled-components';

interface SummaryCardProps extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
    variant: 'package' | 'sku';
    data: PackageType | SkuType | any;
    selected?: boolean;
    onClick: (item: PackageType) => void;
    showPrices?: boolean;
    isDR?: boolean;
    hidePackages?: boolean;
    plainCard?: boolean;
}

function getItems(pkg: any) {
    const subcategories = pkg.subcategories || [];
    return subcategories
        .map((data) => (data && data.default_sku ? data.default_sku.name : ''))
        .filter(baseFilter)
        .join(', ');
}

const SummaryCardComponent = createMemo<SummaryCardProps>(
    (props) => {
        const { variant, data, selected, onClick, showPrices, isDR, hidePackages, plainCard, ...rest } = props;
        const handleClick = useCallback(() => {
            call(onClick, data);
        }, [data]);

        const sku = data as SkuType;
        const pkg = data as PackageType;

        const image = variant === 'package' ? pkg.image : sku.preview_photo;
        const title = variant === 'package' ? pkg.name : sku.name;
        const minimum_order_quantity = variant === 'package' ? pkg.minimum_order_quantity : sku.minimum_order_quantity;
        const price_range = variant === 'package' ? pkg.price_range : sku.price_range;
        const short_description =
            variant === 'package' ? pkg.short_description || getItems(pkg) : sku.short_description;
        rest.className = classList(rest.className, { selected, 'show-prices': showPrices });
        const priceClass = classList('summary-card__price', { selected });

        if (isDR) {
            return <ApparelProductCard selected={selected} isDr product={data} handleClick={handleClick} {...rest} />;
        }

        return (
            <ButtonBase {...rest} onClick={handleClick} TouchRippleProps={{ classes: { root: 'ripple' } }}>
                {!plainCard && <CustomizableWheel />}
                {IF(selected, <Icon className={'summary-card__checked'}>check_circle</Icon>)}
                <Flex w={'100%'} align={'center'} justify={'center'}>
                    <LazyImage
                        className={'summary-card__image'}
                        src={image}
                        thumbnail
                        width={'100%'}
                        height={150}
                        fit={'contain'}
                    />
                </Flex>
                <Typography className={'summary-card__title'} variant={'body2'} title={title}>
                    {title}
                </Typography>

                {!plainCard && (
                    <>
                        {IF(
                            !!short_description,
                            <Typography
                                className={'summary-card__description'}
                                variant={'caption'}
                                title={short_description}
                            >
                                {short_description}
                            </Typography>,
                        )}

                        {IF(
                            !!minimum_order_quantity,
                            <Typography
                                className={'summary-card__minimumOrder'}
                                variant={'caption'}
                                color={'textSecondary'}
                                title={' Minimum Order '}
                            >
                                {minimum_order_quantity + ' Piece Minimum'}
                            </Typography>,
                        )}

                        {IF(
                            showPrices,
                            <Absolute z={1} l={0} b={-1} r={0}>
                                <Typography
                                    className={priceClass}
                                    variant={'caption'}
                                    title={'Low price to High price'}
                                >
                                    <Currency style={{ fontSize: '14px' }}>{price_range.lowest}</Currency>
                                    <InlineFlex margin={'0 6px'}>-</InlineFlex>
                                    <Currency style={{ fontSize: '14px' }}>{price_range.highest}</Currency>
                                </Typography>
                            </Absolute>,
                        )}

                        <Absolute className={'overlay'} z={3} t={0} l={0} b={0} r={0} />
                    </>
                )}
            </ButtonBase>
        );
    },
    {
        displayName: 'SummaryCard',
    },
);

export const SummaryCard = styled(SummaryCardComponent)<SummaryCardProps>`
    && {
        display: flex;
        border: 1px solid #c1c1c1;
        border-radius: 3px;
        width: 100%;
        cursor: pointer;
        padding: 14px 0;
        user-select: none;
        flex-direction: column;
        align-items: center !important;
        align-content: center !important;
        vertical-align: top;
        justify-content: flex-start !important;
        transition: border-color 200ms;
        background-color: #fff;
        z-index: 1;
        position: relative;
        .ripple {
            z-index: 3;
        }
        &.show-prices {
            padding: 14px 0 42px !important;
        }
        .overlay {
            box-shadow: inset 0 0 0 0 transparent;
            transition: box-shadow 200ms;
            pointer-events: none;
        }
        &:hover {
            .overlay {
                box-shadow: inset 0 0 0 4px #c1c1c1;
            }
        }
        &.selected {
            border-color: ${color('primary')};
            .overlay {
                box-shadow: inset 0 0 0 4px ${color('primary')};
            }
        }
        .summary-card {
            &__image,
            .ui--row {
                z-index: 2;
            }

            &__image {
                pointer-events: none;
            }

            &__title,
            &__description {
                width: 100%;
                text-align: center;
                z-index: 1;
            }

            &__title {
                padding: 8px 14px 0 14px;
            }

            &__description {
                padding: 3px 14px 8px;
            }

            &__minimumOrder {
                margin-bottom: 6px;
            }

            &__price {
                width: 100%;
                text-align: center;
                background: #c3c3c3;
                height: 28px;
                display: flex;
                align-content: center;
                align-items: center;
                justify-content: center;
                transition: background-color 200ms;
                &.selected {
                    background-color: ${color('primary')} !important;
                    color: #fff;
                }
            }

            &__checked {
                color: ${color('primary')};
                position: absolute;
                top: 14px;
                right: 16px;
                z-index: 5;
            }
        }
    }
`;
